import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { PATHS } from 'AppPaths';
import clsx from 'clsx';
import { LAPTOP_SCREEN, MOBILE_SCREEN } from 'consts';
import { useStore } from 'effector-react';
import { sendGAEvent } from 'googleAnalytics';
import { useBreadCrumbs, useMatchMedia } from 'hooks';
import { addItemToCart } from 'models/cart';
import {
  address$,
  isFixedReservationBannerVisible$,
  isProductsLoaded$,
  permit$,
  products$,
  setValidPostcode,
  validPostcode$,
} from 'models/product';

import { Meta } from 'features/Meta';

import { CART } from 'api';

import { Description } from './Description';
import { Details } from './Details';
import { FAQ } from './FAQ';
import FixedReservationBar from './FixedReservationBar';
import { Gallery } from './Gallery';
import { MainBlock } from './MainBlock';
import { Meta4, Meta6, Meta8, Meta12, Meta14, Meta16 } from './Metas';
import { PermitModal } from './PermitModal';
import { SimilarProducts } from './SimilarProducts';
import { useCheckReservationBtnPosition } from './useCheckReservationBtnPosition';

import { EnquiryLead } from '../../features/EnquiryLead';
import { Row, Spinner, T8y } from 'ui';

import style from './product.module.scss';

export const Product = () => {
  const products = useStore(products$);
  const isProductsLoaded = useStore(isProductsLoaded$);
  const validPostcode = useStore(validPostcode$);
  const address = useStore(address$);
  const permit = useStore(permit$);
  const isFixedReservationBannerVisible = useStore(
    isFixedReservationBannerVisible$,
  );

  const [selectingDetails, setSelectingDetails] = useState({});
  const [isPermitModalOpen, setIsPermitModalOpen] = useState(false);
  const [isDatesHighlighted, setIsDatesHighlighted] = useState(false);
  const [product, setProduct] = useState(null);
  const [totalPrice, setTotalPrice] = useState(null);
  const [isTotalPriceLoading, setIsTotalPriceLoading] = useState(false);
  const [postcodeError, setPostcodeError] = useState(null);
  const [postcodeSuggestions, setPostcodeSuggestions] = useState(null);
  const [isPostcodeValidating, setIsPostcodeValidating] = useState(false);

  const history = useHistory();
  const { productSlug } = useParams();

  const isLaptop = useMatchMedia(`(max-width: ${LAPTOP_SCREEN}px)`);
  const isMobile = useMatchMedia(`(max-width: ${MOBILE_SCREEN}px)`);

  const enquiryWrapperRef = useRef(null);
  const mainBtnRef = useRef(null);

  const breadCrumbs = useMemo(
    () => ({
      products: true,
      product: { slug: product?.slug, name: product?.name },
    }),
    [product],
  );

  useBreadCrumbs(breadCrumbs);

  const datesRef = useRef(null);

  const isShowPrice =
    selectingDetails.postcode && typeof selectingDetails.permit === 'string';

  useEffect(() => {
    const product = products.find((el) => el.slug === productSlug);

    setProduct(product);

    if (!product && isProductsLoaded) {
      history.replace(PATHS.ROOT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSlug, isProductsLoaded]);

  useEffect(
    () => {
      if (product) {
        setSelectingDetails({
          product,
          id: product?.id,
          count: 1,
          delivery: 'delivery',
          postcode: validPostcode,
          fullAddress: address,
          permit,
        });
        if (validPostcode) {
          setIsPostcodeValidating(true);
        }
        setTotalPrice(null);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [product],
  );

  useLayoutEffect(() => {
    if (selectingDetails.postcode) {
      setIsPostcodeValidating(true);
    }
  }, [selectingDetails.postcode]);

  useEffect(
    () => {
      if (!selectingDetails.postcode) {
        setPostcodeError(null);
        setPostcodeSuggestions([]);
      }

      if (selectingDetails.postcode) {
        setIsTotalPriceLoading(true);

        CART.getTotalPrice({
          ...selectingDetails,
          products: [
            {
              ...selectingDetails.product,
              quantity: selectingDetails.count,
              delivery: selectingDetails.delivery,
              permitEnabled: selectingDetails.permit === 'yes',
            },
          ],
        })
          .then((res) => {
            // проверяем на ошибку
            if (res.data?.data?.nonAvailableProducts) {
              setPostcodeError(
                "Sorry, we're unable to fetch pricing for this product at the moment.",
              );

              setPostcodeSuggestions(res.data?.data?.availableProducts || []);
              setValidPostcode({
                postcode: selectingDetails.postcode,
                address: selectingDetails.fullAddress,
              });

              return;
            }

            if (isShowPrice) {
              setTotalPrice(res.data?.data);
            }
            setPostcodeError(null);
            setPostcodeSuggestions([]);
            setIsPostcodeValidating(false);
            setValidPostcode({
              postcode: selectingDetails.postcode,
              address: selectingDetails.fullAddress,
            });
          })
          .catch((err) => {
            if (
              err.response?.data?.message === 'Postcode not found' ||
              err.response?.data?.message ===
                'This product is not available for this postcode'
            ) {
              setPostcodeError(
                "Sorry, we're unable to fetch pricing for this product at the moment.",
              );
            } else {
              setPostcodeError('Something went wrong.\nPlease try again later');
              setPostcodeSuggestions([]);
            }
          })
          .finally(() => {
            setIsTotalPriceLoading(false);
            setIsPostcodeValidating(false);
          });
      } else {
        setTotalPrice(null);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isShowPrice,
      selectingDetails.product,
      selectingDetails.count,
      selectingDetails.delivery,
      selectingDetails.permit,
      selectingDetails.postcode,
    ],
  );

  const scrollToDates = useCallback(() => {
    const datesPosition = datesRef.current.getBoundingClientRect().top;
    const offsetPosition = datesPosition + window.pageYOffset - 200;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }, []);

  const handleAddProduct = useCallback(() => {
    if (!selectingDetails.dates?.start || !selectingDetails.dates?.end) {
      setIsDatesHighlighted(true);
      setTimeout(() => setIsDatesHighlighted(false), 2000);

      if (isMobile) {
        scrollToDates();
      }
      return;
    }
    addItemToCart({
      ...selectingDetails,
      id: product?.id,
    });

    sendGAEvent(
      'add_to_cart',
      [
        {
          item_id: product?.id,
          item_name: product?.name,
          coupon: '',
          discount: 0,
          item_category: 'skip',
          location_id: validPostcode,
          price: totalPrice?.subtotal,
          quantity: selectingDetails.count,
          currency: 'GBP',
        },
      ],
      {
        transaction_id: '',
        value: totalPrice?.subtotal,
        tax: totalPrice?.tax,
        shipping: 0,
        currency: 'GBP',
        coupon: '',
      },
      totalPrice,
    );

    history.push(PATHS.RESERVATION);
  }, [
    selectingDetails,
    product,
    validPostcode,
    totalPrice,
    history,
    isMobile,
    scrollToDates,
  ]);

  useCheckReservationBtnPosition(enquiryWrapperRef, mainBtnRef);

  const ProductMeta = useMemo(() => {
    switch (productSlug) {
      case '4-yard-skip':
        return Meta4;
      case '6-yard-skip':
        return Meta6;
      case '8-yard-skip':
        return Meta8;
      case '12-yard-skip':
        return Meta12;
      case '14-yard-skip':
        return Meta14;
      case '16-yard-skip':
        return Meta16;
      default:
        return Meta4;
    }
  }, [productSlug]);

  if (!product) {
    return (
      <div className={style.productLoading}>
        <Spinner size={5} />
      </div>
    );
  }

  return (
    <div>
      <Meta
        title={product?.meta?.title || `${product?.name} - Skip hire`}
        description={product?.meta?.description}
      />
      <div className={style.container}>
        <Row fullWidth>
          <div className={clsx(style.productLeftSide, style.productGallery)}>
            <Gallery product={product} />
          </div>

          <div className={style.productRightSide}>
            <T8y
              variant="h1"
              lgVariant="h2"
              mdVariant="h3"
              as="h1"
              mb2
              className={style.productName}
            >
              {product?.seo_h1 || product?.name || ''}
            </T8y>
            <MainBlock
              setIsPermitModalOpen={setIsPermitModalOpen}
              selectingDetails={selectingDetails}
              setSelectingDetails={setSelectingDetails}
              handleAddProduct={handleAddProduct}
              isDatesHighlighted={isDatesHighlighted}
              setIsDatesHighlighted={setIsDatesHighlighted}
              datesRef={datesRef}
              mainBtnRef={mainBtnRef}
              product={product}
              price={totalPrice?.subtotal}
              postcodeError={postcodeError}
              isBookNowDisabled={!isShowPrice || isTotalPriceLoading}
              isTotalPriceLoading={isTotalPriceLoading}
              isPostcodeValidating={isPostcodeValidating}
              postcodeSuggestions={postcodeSuggestions}
            />
          </div>
          <div className={style.productLeftSide}>
            {isLaptop ? (
              <Details
                details={product.details}
                dimensions={product.dimensions}
              />
            ) : (
              <Description description={product?.description} />
            )}
          </div>
          <div className={style.productRightSide}>
            {isLaptop ? (
              <Description description={product?.description} />
            ) : (
              <Details
                details={product.details}
                dimensions={product.dimensions}
              />
            )}
            <FAQ product={product} />
          </div>
        </Row>
      </div>
      <SimilarProducts currentProductId={product.id} />
      <div className={style.container}>
        <ProductMeta />
      </div>
      {/*<EnquiryLead*/}
      {/*  source="Contact form (product page)"*/}
      {/*  innerRef={enquiryWrapperRef}*/}
      {/*/>*/}
      {isPermitModalOpen && (
        <PermitModal setIsPermitModalOpen={setIsPermitModalOpen} />
      )}
      {isFixedReservationBannerVisible && !isPermitModalOpen && (
        <FixedReservationBar
          handleAddProduct={handleAddProduct}
          product={product}
          postcodeError={postcodeError}
          isBookNowDisabled={!isShowPrice || isTotalPriceLoading}
          calculatedPrice={totalPrice?.subtotal || 'TBD'}
        />
      )}
    </div>
  );
};

export default Product;
